/** Set15 P0 (Features/Set15_3DMode.md D11): a viewpoint = the position + orientation of the
 *  locked 3D camera.
 *
 *  It lives as a modifier on a `MapObject` (mirroring `Mod_TerrainSpotHeight`): the XY position
 *  comes from the host object's anchor via `ResolveObjPosInWorld`; this class carries the
 *  camera's height and orientation. In 3D (P1+) it becomes a `PerspectiveCamera`; in 2D (P0) a
 *  ground-projected frustum wedge is drawn so viewpoints can be aimed without entering 3D.
 *
 *  All numeric fields here are user-facing and resolved through validated defaults before any
 *  comparison (Set15 §8 R1). */

export class Mod_Viewpoint {
	constructor(data?: Partial<Mod_Viewpoint>) { Object.assign(this, data); }

	/** Camera eye height (metres), ABSOLUTE world z. The camera reads this directly — stable and
	 *  independent of terrain height, so a terrain solve never moves the camera (Set15 P5). BAKED:
	 *  the UI edits {@link eyeHeightAboveTerrain_target} and "Apply" writes the computed height here. */
	eyeHeight = 100;
	/** UI-only TARGET height above terrain (metres); NOT read by the camera. The Viewpoint UI shows
	 *  this and "Apply" sets {@link eyeHeight} = terrainHeightAtAnchor + target — so editing the
	 *  target never auto-churns the camera. */
	eyeHeightAboveTerrain_target: number|n = 1.6;
	/** Camera yaw (degrees), rotation about world z, measured from map +y ("north"/forward).
	 *  0 = looking toward +y. The 2D frustum wedge points along this. */
	yaw = 0;
	/** Camera pitch (degrees). 0 = horizontal; >0 = looking down toward the terrain. This sets
	 *  how far the frustum wedge reaches along the ground plane. */
	pitch = 40;
	/** How far from the camera (metres), toward its horizontal/compass direction, to place the
	 *  detailed-height-solving region's centre.  The solver (working grid / ring stack) centres on
	 *  `eye + yawDirection × this` instead of the eye, so resolution is spent where the camera is
	 *  LOOKING rather than directly under it.  Concretely visualisable in the 2D frustum wedge.
	 *  Default 0 = centre the solve on the eye (the historical behaviour). */
	detailedSolveCenter_distance = 0;
	/** PER-VIEWPOINT override of the global `renderChunk_maxSamplesPerFineCellEdge` (Set15.5 P2 — the
	 *  render-lattice FLOOR, samples per solved fine-cell edge, clamped `[0.25, 64]`).  `null` (default)
	 *  = fall back to the global `MapSettings_Terrain` value.  Set this HIGHER than the global default
	 *  for a viewpoint that sits very close to the ground (where the default lattice would be too coarse
	 *  to resolve near-field detail), or LOWER to trade resolution for cost on a far/wide viewpoint.
	 *  Resolved through the same validated clamp as the global knob (§8 R1 — never a bare comparison). */
	renderChunk_maxSamplesPerFineCellEdge: number|n = null;
	/** Vertical field of view (degrees). Wider = more of the scene visible = a wider wedge. */
	fov = 60;
	/** Camera roll (degrees), rotation about the view axis. 0 = level horizon. Rarely needed. */
	roll = 0;
}
